/**
 * TypeScript view of the `countdown` format.
 * Source of truth: `shared/schema/countdown.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;

export interface CountdownDefinition {
  puzzleType: "countdown";
  typeVersion: number;
  /** The given numbers, in display order. A value may repeat: each copy is one use. */
  numbers: number[];
  target: number;
  /** Every number exactly once (the 24 game); otherwise each at most once. */
  useAll?: boolean;
  /** Intermediate results may be any rational; otherwise each must be a positive integer. */
  allowFractions?: boolean;
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface CountdownProgress {
  version: 1;
  expression: string;
}

export interface CountdownSubmission {
  expression: string;
}

/** The author's expression, in the submission format. */
export type CountdownSolution = CountdownSubmission;

export const MIN_NUMBERS = 2;
export const MAX_NUMBERS = 8;
export const MAX_NUMBER = 1000;
export const MAX_TARGET = 99999;
export const MAX_EXPRESSION = 200;

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
